/**
 * Playbook display helpers shared by the AI agents settings card and the Repairs / Leasing links.
 */

import {
  PLAYBOOK_TEXT_MAX,
  type AgentPlaybookKindView,
  type PolicyAgentKind,
  type PlaybookDefaultView,
  type RuleScope,
} from '@resida/api-types/agent';
import type { ImprovementProposalView } from '../api/improvement-conversations.api';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * One-line summary of a saved playbook: "2 of your rules · 1 default off · 1 reworded"
 * (zero parts omitted), or "Following Resida's defaults." when nothing is customized.
 */
export function summarizePlaybook(
  view: Pick<AgentPlaybookKindView, 'customized' | 'rules' | 'defaults'>,
): string {
  const rules = view.rules.length;
  const off = view.defaults.filter((d) => d.state === 'off' && !d.retired).length;
  const reworded = view.defaults.filter((d) => d.state === 'rewritten' && !d.retired).length;
  const parts = [
    rules > 0 ? `${rules} of your rules` : null,
    off > 0 ? plural(off, 'default off', 'defaults off') : null,
    reworded > 0 ? `${reworded} reworded` : null,
  ].filter((p): p is string => p !== null);
  return view.customized && parts.length > 0 ? parts.join(' · ') : "Following Resida's defaults.";
}

/**
 * Whether the playbook indicator reads "Playbook customized" (BR-SETTINGS-AGENTS-INDICATOR): the
 * landlord document is customized, or some property document has a rule, a default turned off or
 * reworded, or a default turned on.
 */
export function isPlaybookCustomized(view: Pick<AgentPlaybookKindView, 'customized' | 'propertyScopes'>): boolean {
  return (
    view.customized ||
    view.propertyScopes.some(
      (p) =>
        p.counts.rules > 0 ||
        p.counts.defaultsOff > 0 ||
        p.counts.defaultsReworded > 0 ||
        p.defaults.some((d) => d.state === 'on'),
    )
  );
}

/** "2 properties with their own rules" (BR-SETTINGS-AGENTS-SUMMARY); null when no property has a document. */
export function propertyRulesSummary(view: Pick<AgentPlaybookKindView, 'propertyScopes'>): string | null {
  const n = view.propertyScopes.length;
  if (n === 0) return null;
  return n === 1 ? '1 property with its own rules' : `${n} properties with their own rules`;
}

/** Lower-case noun for an agent kind: "repair" / "leasing" / "reference" (used as "your repair agent"). */
export const AGENT_KIND_NOUN: Record<PolicyAgentKind, string> = {
  repair: 'repair',
  leasing: 'leasing',
  reference_coordinator: 'reference',
};

/** Display name for an agent kind: "Repair agent" / "Leasing agent" / "Reference agent". */
export function agentName(kind: PolicyAgentKind): string {
  const noun = AGENT_KIND_NOUN[kind];
  return `${noun[0]!.toUpperCase()}${noun.slice(1)} agent`;
}

/**
 * Display name for an agent kind slug as the queues receive it (any string): the same names
 * Settings uses, so a landlord meets one name per agent. An unknown slug is title-cased.
 */
export function agentNameForSlug(slug: string): string {
  if (Object.prototype.hasOwnProperty.call(AGENT_KIND_NOUN, slug)) return agentName(slug as PolicyAgentKind);
  const words = slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return `${words} agent`;
}

export interface PlaybookErrorMap {
  /** Message per draft rule index. */
  rules: Record<number, string>;
  /** Message per customized default item id. */
  defaults: Record<string, string>;
  /** Messages that name no single row. */
  general: string[];
}

/**
 * Map the save route's 422 `errors[].path` (`rules[3].text`, `defaults[1].itemId`, `rules`) onto the
 * draft's rows, with a message that names the row. Indexes refer to the body sent, which is the
 * draft in order.
 */
export function mapPlaybookErrors(
  errors: ReadonlyArray<{ path: string; message: string }>,
  draft: { rules: ReadonlyArray<{ text: string }>; defaults: ReadonlyArray<{ itemId: string }> },
): PlaybookErrorMap {
  const map: PlaybookErrorMap = { rules: {}, defaults: {}, general: [] };
  const nothingSaved = (message: string) => `${message.replace(/\.$/, '')}. Nothing was saved.`;
  for (const { path, message } of errors) {
    const rule = /^rules\[(\d+)\]/.exec(path);
    const def = /^defaults\[(\d+)\]/.exec(path);
    const ruleIndex = rule ? Number(rule[1]) : -1;
    const defaultItem = def ? draft.defaults[Number(def[1])] : undefined;
    if (rule && draft.rules[ruleIndex]) {
      const length = draft.rules[ruleIndex]!.text.length;
      map.rules[ruleIndex] =
        length > PLAYBOOK_TEXT_MAX
          ? `Rule ${ruleIndex + 1} is ${length} characters — keep each rule under ${PLAYBOOK_TEXT_MAX}. Nothing was saved.`
          : nothingSaved(`Rule ${ruleIndex + 1}: ${message}`);
    } else if (defaultItem) {
      map.defaults[defaultItem.itemId] = nothingSaved(message);
    } else {
      map.general.push(nothingSaved(message));
    }
  }
  return map;
}

type RuleScopeLike = RuleScope;

/** "12 Elm St only" / "All your properties". */
export function scopeChipLabel(scope: RuleScopeLike, propertyName: string | undefined): string {
  return scope.kind === 'landlord' ? 'All your properties' : `${propertyName ?? 'One property'} only`;
}

/** Where the rule applies, as a phrase: "at 12 Elm St" / "at all your properties". */
export function scopePhrase(scope: RuleScopeLike, propertyName: string | undefined): string {
  return scope.kind === 'landlord' ? 'at all your properties' : `at ${propertyName ?? 'that property'}`;
}

/** The rule's own text when the operations carry it, else the agent's one-line summary. */
export function proposalRuleText(proposal: Pick<ImprovementProposalView, 'operations' | 'summary'>): string {
  const texts = proposal.operations.map((o) => o.text).filter((t): t is string => Boolean(t));
  return texts.length > 0 ? texts.join(' ') : proposal.summary;
}

/** Counts of a saved scope's defaults the way the playbook read reports them (retired items don't count). */
export function countDefaults(defaults: readonly PlaybookDefaultView[]): { defaultsOff: number; defaultsReworded: number } {
  return {
    defaultsOff: defaults.filter((d) => d.state === 'off' && !d.retired).length,
    defaultsReworded: defaults.filter((d) => d.state === 'rewritten' && !d.retired).length,
  };
}
